/**
 * The wording of the bathymetry PDF, kept apart from the builder that lays it out.
 * {@link buildBathymetryReport} is a pure function of the analysis - no translator, no clock -
 * which is what lets the whole report be checked without a browser. Both of those live here
 * instead, in the one place the report reaches outside itself.
 */
/**
 * @constant TEXT_KEYS
 * @description The wording the report asks for by name, each one a key under the module's
 * `pdfReport` block. Listed rather than written out one assignment at a time: they are all
 * translated the same way, and a list is what a missing one can be checked against.
 * @memberof module:Bathymetry
 */
const TEXT_KEYS = [
  'title',
  'footer',
  'areaHeading',
  'areaFile',
  'areaExtent',
  'crs',
  'soundingsFile',
  'soundings',
  'surveyedRange',
  'surveyDate',
  'imageryHeading',
  'mission',
  'resolution',
  'processing',
  'period',
  'waterMask',
  'waterThreshold',
  'imageCount',
  'imageList',
  'modelHeading',
  'model',
  'bands',
  'extinctionDepth',
  'trainingReducer',
  'validationSplit',
  'validationSplitOn',
  'validationSplitOff',
  'predictorSet',
  'seed',
  'presetNote',
  'metricsHeading',
  'metricsNote',
  'binsHeading',
  'binsNote',
  'overallRmse',
  'warningsHeading',
  'warningsNote',
  'mapsHeading',
  'depthCaption',
  'imageCaption',
];

/**
 * @function reportText
 * @description Every piece of wording the report needs, translated once and handed to
 * {@link buildBathymetryReport}, which stays a pure function of the analysis and holds no
 * knowledge of the language it will be read in.
 * @param {Function} t - The translator.
 * @param {Object} [data] - The analysis configuration, for the subtitle.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, on the PDF export.
 * @memberof module:Bathymetry
 * @returns {Object} The wording, by name.
 */
export const reportText = (t, data) => {
  const key = (name) => `forms.acquisition.5.bathymetry.pdfReport.${name}`;
  return {
    ...Object.fromEntries(TEXT_KEYS.map((name) => [name, t(key(name))])),
    subtitle: t(key('subtitle'), { model: data?.modelSelected ?? '—', date: new Date().toLocaleString() }),
    missingImage: (caption) => t(key('missingImage'), { caption }),
    staleYears: 2,
    thinLimit: 20,
    staleWarning: (values) => t(key('staleWarning'), values),
    thinWarning: (values) => t(key('thinWarning'), values),
    binChart: t(key('binChart'), { returnObjects: true }),
    binColumns: {
      band: t(key('binBand')),
      count: t(key('binCount')),
      rmse: t(key('binRmse')),
      mae: t(key('binMae')),
      bias: t(key('binBias')),
    },
  };
};
